import toast from "@/components/ui/toast";
import {
  CreateFeedSavedViewMutation,
  DeleteFeedSavedViewMutation,
  GetSavedViews,
  type SavedViewItem,
  ToggleSavedViewUserStar,
  UpdateFeedSavedViewMutation,
} from "@/data-access/saved-views/queries/saved-views-queries";
import { client } from "@/lib/urqlProvider";
import type { FeedArticleFilterInput } from "@/types/shared";
import useSavedViewStore from "./saved-views-management.slice";

/**
 * Type guard to check if response is an OperationInfo error
 */
const isOperationInfo = (
  response: unknown,
): response is {
  __typename: "OperationInfo";
  messages?: Array<{ message?: string }>;
} => {
  return (
    typeof response === "object" &&
    response !== null &&
    "__typename" in response &&
    response.__typename === "OperationInfo"
  );
};

/**
 * Type guard to check if response is a valid saved view
 */
const isSavedView = (response: unknown): response is SavedViewItem => {
  return (
    typeof response === "object" &&
    response !== null &&
    "id" in response &&
    typeof response.id === "string" &&
    response.id.length > 0
  );
};

/**
 * Fetches all saved views for a specific feed
 */
export const getSavedViews = async (feedId: string): Promise<boolean> => {
  useSavedViewStore.setState(
    { isFetchingSavedViews: true },
    false,
    "[User] SavedView: Fetching saved views",
  );

  try {
    const result = await client
      .query(GetSavedViews, { filters: { feedId } })
      .toPromise();

    if (result.error) {
      console.error("Error fetching saved views:", result.error);
      toast.error("Failed to fetch saved views.");
      useSavedViewStore.setState(
        { isFetchingSavedViews: false },
        false,
        "[User] SavedView: Fetch saved views error",
      );
      return false;
    }

    const savedViews = result.data?.feedSavedViews || [];
    const savedViewsMap = new Map(savedViews.map((view) => [view.id, view]));

    useSavedViewStore.setState(
      {
        isFetchingSavedViews: false,
        allSavedViews: savedViewsMap,
      },
      false,
      "[User] SavedView: Fetch saved views success",
    );

    return true;
  } catch (error) {
    console.error("Error fetching saved views:", error);
    toast.error("An error occurred while fetching saved views.");
    useSavedViewStore.setState(
      { isFetchingSavedViews: false },
      false,
      "[User] SavedView: Fetch saved views error",
    );
    return false;
  }
};

/**
 * Creates a new saved view
 */
export const createSavedView = async (
  feedId: string,
  // Deprecated parameter, will be removed in the future
  urlSelectedFilters = "",
  feedArticleFilter?: FeedArticleFilterInput,
): Promise<boolean> => {
  const { name } = useSavedViewStore.getState();

  if (!name || !feedId) {
    return false;
  }

  useSavedViewStore.setState(
    { isCreatingSavedView: true },
    false,
    "[User] SavedView: Creating saved view",
  );

  try {
    const result = await client
      .mutation(CreateFeedSavedViewMutation, {
        input: {
          feedId,
          name,
          urlSelectedFilters,
          feedArticleFilter,
        },
      })
      .toPromise();

    if (result.error) {
      console.error("Error creating saved view:", result.error);
      toast.error("Failed to create saved view.");
      useSavedViewStore.setState(
        { isCreatingSavedView: false },
        false,
        "[User] SavedView: Create saved view error",
      );
      return false;
    }

    const createdSavedView = result.data?.createFeedSavedView;

    // Check if creation failed with OperationInfo error
    if (isOperationInfo(createdSavedView)) {
      const errorMessage =
        createdSavedView.messages?.[0]?.message ||
        "Failed to create saved view.";
      console.error("Error creating saved view:", errorMessage);
      toast.error(errorMessage);
      useSavedViewStore.setState(
        { isCreatingSavedView: false },
        false,
        "[User] SavedView: Create saved view error",
      );
      return false;
    }

    // Check if we got a valid saved view back (has id and name)
    if (!isSavedView(createdSavedView)) {
      console.error("Error creating saved view: Invalid response structure");
      toast.error("Failed to create saved view.");
      useSavedViewStore.setState(
        { isCreatingSavedView: false },
        false,
        "[User] SavedView: Create saved view error",
      );
      return false;
    }

    // Add the newly created saved view to the existing saved views map
    const { allSavedViews: currentSavedViews } = useSavedViewStore.getState();
    const updatedSavedViews = new Map(currentSavedViews);
    updatedSavedViews.set(createdSavedView.id, createdSavedView);

    useSavedViewStore.setState(
      {
        isCreatingSavedView: false,
        createEditSavedViewState: "complete",
        allSavedViews: updatedSavedViews,
      },
      false,
      "[User] SavedView: Create saved view success",
    );

    // Reset saved view state after successful creation
    useSavedViewStore.getState().resetCreateEditSavedView();

    // Select the newly created saved view
    useSavedViewStore.setState(
      { selectedSavedViewId: createdSavedView.id },
      false,
      "[User] SavedView: Select newly created saved view",
    );

    toast({
      type: "success",
      title: "Saved view created!",
      description: `Saved view "${name}" has been created successfully.`,
    });

    return true;
  } catch (error) {
    console.error("Error creating saved view:", error);
    toast.error("An error occurred while creating the saved view.");
    useSavedViewStore.setState(
      { isCreatingSavedView: false },
      false,
      "[User] SavedView: Create saved view error",
    );
    return false;
  }
};

/**
 * Partial update options for a saved view.
 * Only provide the fields you want to update.
 */
type UpdateSavedViewOptions = {
  name?: string;
  urlSelectedFilters?: string;
  feedArticleFilter?: FeedArticleFilterInput;
};

/**
 * Updates an existing saved view with partial updates support.
 * You can update individual fields without affecting others.
 *
 * @example
 * // Update only the name
 * await updateSavedView({ name: 'New Name' });
 *
 * @example
 * // Update only the filters
 * await updateSavedView({ feedArticleFilter: {...} });
 *
 * @example
 * // Update multiple fields
 * await updateSavedView({ name: 'New Name', feedArticleFilter: {...} });
 */
export const updateSavedView = async (
  options: UpdateSavedViewOptions = {},
): Promise<boolean> => {
  const { selectedSavedViewId } = useSavedViewStore.getState();

  if (!selectedSavedViewId) {
    return false;
  }

  // Build the update payload with only the id and fields that are explicitly provided
  const updatePayload: {
    id: string;
    name?: string;
    urlSelectedFilters?: string;
    feedArticleFilter?: FeedArticleFilterInput;
  } = {
    id: selectedSavedViewId,
    ...(options.name !== undefined && { name: options.name }),
    ...(options.urlSelectedFilters !== undefined && {
      urlSelectedFilters: options.urlSelectedFilters,
    }),
    ...(options.feedArticleFilter !== undefined && {
      feedArticleFilter: options.feedArticleFilter,
    }),
  };

  useSavedViewStore.setState(
    { isUpdatingSavedView: true },
    false,
    "[User] SavedView: Updating saved view",
  );

  try {
    const result = await client
      .mutation(UpdateFeedSavedViewMutation, {
        input: updatePayload,
      })
      .toPromise();

    if (result.error) {
      console.error("Error updating saved view:", result.error);
      toast.error("Failed to update saved view.");
      useSavedViewStore.setState(
        { isUpdatingSavedView: false },
        false,
        "[User] SavedView: Update saved view error",
      );
      return false;
    }

    const updatedSavedView = result.data?.updateFeedSavedView;

    // Check if update failed with OperationInfo error
    if (isOperationInfo(updatedSavedView)) {
      const errorMessage =
        updatedSavedView.messages?.[0]?.message ||
        "Failed to update saved view.";
      console.error("Error updating saved view:", errorMessage);
      toast.error(errorMessage);
      useSavedViewStore.setState(
        { isUpdatingSavedView: false },
        false,
        "[User] SavedView: Update saved view error",
      );
      return false;
    }

    // Check if we got a valid saved view back (has id and name)
    if (!isSavedView(updatedSavedView)) {
      console.error("Error updating saved view: Invalid response structure");
      toast.error("Failed to update saved view.");
      useSavedViewStore.setState(
        { isUpdatingSavedView: false },
        false,
        "[User] SavedView: Update saved view error",
      );
      return false;
    }

    // Update the saved view in the store's map
    const { allSavedViews: currentSavedViews } = useSavedViewStore.getState();
    const updatedSavedViews = new Map(currentSavedViews);
    updatedSavedViews.set(updatedSavedView.id, updatedSavedView);

    useSavedViewStore.setState(
      {
        isUpdatingSavedView: false,
        createEditSavedViewState: "complete",
        allSavedViews: updatedSavedViews,
      },
      false,
      "[User] SavedView: Update saved view success",
    );

    // Reset saved view state after successful update
    useSavedViewStore.getState().resetCreateEditSavedView();

    toast({
      type: "success",
      title: "Saved view updated!",
      description: `Saved view "${updatedSavedView.name}" has been updated successfully.`,
    });

    return true;
  } catch (error) {
    console.error("Error updating saved view:", error);
    toast.error("An error occurred while updating the saved view.");
    useSavedViewStore.setState(
      { isUpdatingSavedView: false },
      false,
      "[User] SavedView: Update saved view error",
    );
    return false;
  }
};

/**
 * Deletes the currently selected saved view
 * @returns Promise<boolean> - Returns true if successful, false otherwise
 */
export const deleteSavedView = async (): Promise<boolean> => {
  const { selectedSavedViewId } = useSavedViewStore.getState();

  if (!selectedSavedViewId) {
    return false;
  }

  useSavedViewStore.setState(
    { isDeletingSavedView: true },
    false,
    "[User] SavedView: Deleting saved view",
  );

  try {
    const result = await client
      .mutation(DeleteFeedSavedViewMutation, {
        input: {
          savedViewId: selectedSavedViewId,
        },
      })
      .toPromise();

    if (result.error) {
      console.error("Error deleting saved view:", result.error);
      toast.error("Failed to delete saved view.");
      useSavedViewStore.setState(
        { isDeletingSavedView: false },
        false,
        "[User] SavedView: Delete saved view error",
      );
      return false;
    }

    const deleteResponse = result.data?.deleteFeedSavedView;

    // Check if deletion was successful
    if (isOperationInfo(deleteResponse)) {
      const errorMessage =
        deleteResponse.messages?.[0]?.message || "Failed to delete saved view.";
      console.error("Error deleting saved view:", errorMessage);
      toast.error(errorMessage);
      useSavedViewStore.setState(
        { isDeletingSavedView: false },
        false,
        "[User] SavedView: Delete saved view error",
      );
      return false;
    }

    // Remove the deleted saved view from the store's map
    const { allSavedViews: currentSavedViews } = useSavedViewStore.getState();
    const updatedSavedViews = new Map(currentSavedViews);
    updatedSavedViews.delete(selectedSavedViewId);

    useSavedViewStore.setState(
      {
        isDeletingSavedView: false,
        allSavedViews: updatedSavedViews,
        isDeleteSavedViewModalOpen: false,
        selectedSavedViewId: null, // Clear the selected view since we just deleted it
      },
      false,
      "[User] SavedView: Delete saved view success",
    );

    // Reset delete state
    useSavedViewStore.getState().resetDeleteSavedView();

    toast({
      type: "success",
      title: "Saved view deleted!",
      description: "The saved view has been deleted successfully.",
    });

    return true;
  } catch (error) {
    console.error("Error deleting saved view:", error);
    toast.error("An error occurred while deleting the saved view.");
    useSavedViewStore.setState(
      { isDeletingSavedView: false },
      false,
      "[User] SavedView: Delete saved view error",
    );
    return false;
  }
};

/**
 * Stars or unstars a saved view for the current user.
 */
export const toggleSavedViewUserStar = async (
  savedViewId: string,
  starred: boolean,
): Promise<boolean> => {
  if (!savedViewId) {
    return false;
  }

  try {
    const result = await client
      .mutation(ToggleSavedViewUserStar, {
        input: {
          savedViewId,
          starred,
        },
      })
      .toPromise();

    if (result.error) {
      console.error("Error toggling saved view star:", result.error);
      toast.error("Failed to update favorites.");
      return false;
    }

    const toggledSavedView = result.data?.toggleSavedViewUserStar;

    if (isOperationInfo(toggledSavedView)) {
      const errorMessage =
        toggledSavedView.messages?.[0]?.message ||
        "Failed to update favorites.";
      console.error("Error toggling saved view star:", errorMessage);
      toast.error(errorMessage);
      return false;
    }

    if (!isSavedView(toggledSavedView)) {
      console.error(
        "Error toggling saved view star: Invalid response structure",
      );
      toast.error("Failed to update favorites.");
      return false;
    }

    const { allSavedViews: currentSavedViews } = useSavedViewStore.getState();
    const updatedSavedViews = new Map(currentSavedViews);
    updatedSavedViews.set(toggledSavedView.id, toggledSavedView);

    useSavedViewStore.setState(
      { allSavedViews: updatedSavedViews },
      false,
      "[User] SavedView: Toggle user star success",
    );

    return true;
  } catch (error) {
    console.error("Error toggling saved view star:", error);
    toast.error("An error occurred while updating favorites.");
    return false;
  }
};
